import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  STATUS,
  getStatusConfig,
  statusDoMembro,
  temCoordenadas,
} from './analiseFrequenciaHelpers';

/** Paleta alinhada ao Excel premium */
const RGB = {
  navy: [30, 58, 95],
  navyDark: [15, 39, 68],
  teal: [15, 118, 110],
  tealLight: [204, 251, 241],
  white: [255, 255, 255],
  ink: [30, 41, 59],
  muted: [100, 116, 139],
  soft: [248, 250, 252],
  softAlt: [241, 245, 249],
  border: [203, 213, 225],
  green: [21, 128, 61],
  greenBg: [220, 252, 231],
  greenSoft: [240, 253, 244],
  amber: [180, 83, 9],
  amberBg: [254, 243, 199],
  red: [185, 28, 28],
  redBg: [254, 226, 226],
  slate: [71, 85, 105],
  slateBg: [226, 232, 240],
  violet: [109, 40, 217],
  violetBg: [237, 233, 254],
  gold: [161, 98, 7],
  goldBg: [254, 249, 195],
  blue: [29, 78, 216],
  blueBg: [219, 234, 254],
};

const STATUS_STYLE = {
  Frequente: { fg: RGB.green, bg: RGB.greenBg },
  Comparece: { fg: [22, 101, 52], bg: RGB.greenSoft },
  Irregular: { fg: RGB.amber, bg: RGB.amberBg },
  'Não comparece': { fg: RGB.red, bg: RGB.redBg },
  'Não informado': { fg: RGB.slate, bg: RGB.slateBg },
  Inconsistente: { fg: RGB.violet, bg: RGB.violetBg },
};

const pct = (v) => (v == null ? '—' : `${v}%`);
const simNao = (v) => (v === true ? 'Sim' : v === false ? 'Não' : '—');
const catLabel = (m) => (m.categoria === 'crianca' ? 'Criança' : 'Jovem');
const statusLabel = (m) => getStatusConfig(statusDoMembro(m)).label;
const telefoneDe = (m) => m.responsavel_telefone || m.telefone || '—';
const bairroDe = (m) => {
  const b = String(m.bairro || '').trim();
  if (!b || ['não identificado', 'nao identificado', '—', '-'].includes(b.toLowerCase())) {
    return 'sem endereço';
  }
  return b;
};
const enderecoDe = (m) => {
  const e = String(m.endereco || '').trim();
  if (!e || ['—', '-', '--'].includes(e)) return 'sem endereço';
  return e;
};

function styleStatus(text) {
  return STATUS_STYLE[text] || null;
}

function styleTri(text) {
  if (text === 'Sim') return { fg: RGB.green, bg: RGB.greenBg };
  if (text === 'Não') return { fg: RGB.red, bg: RGB.redBg };
  if (text === '—') return { fg: RGB.muted, bg: RGB.softAlt };
  return null;
}

function styleCat(text) {
  if (text === 'Criança') return { fg: RGB.blue, bg: RGB.blueBg };
  if (text === 'Jovem') return { fg: RGB.gold, bg: RGB.goldBg };
  return null;
}

function styleTaxa(text, modo = 'comparecimento') {
  if (!text || text === '—') return { fg: RGB.muted, bg: RGB.softAlt };
  const n = Number(String(text).replace('%', '').replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  if (modo === 'atencao') {
    if (n >= 40) return { fg: RGB.red, bg: RGB.redBg };
    if (n >= 20) return { fg: RGB.amber, bg: RGB.amberBg };
    return { fg: RGB.green, bg: RGB.greenBg };
  }
  if (n >= 70) return { fg: RGB.green, bg: RGB.greenBg };
  if (n >= 40) return { fg: RGB.amber, bg: RGB.amberBg };
  return { fg: RGB.red, bg: RGB.redBg };
}

function aplicarEstilo(cell, estilo, { bold = true, center = true } = {}) {
  if (!estilo) return;
  cell.styles.fillColor = estilo.bg;
  cell.styles.textColor = estilo.fg;
  if (bold) cell.styles.fontStyle = 'bold';
  if (center) cell.styles.halign = 'center';
}

function metricasGrupo(lista = []) {
  const contagem = {
    [STATUS.FREQUENTE]: 0,
    [STATUS.COMPARECE]: 0,
    [STATUS.IRREGULAR]: 0,
    [STATUS.NAO_COMPARECE]: 0,
    [STATUS.NAO_INFORMADO]: 0,
    [STATUS.DADO_INCONSISTENTE]: 0,
  };
  lista.forEach((m) => { contagem[statusDoMembro(m)] += 1; });
  const frequentes = contagem[STATUS.FREQUENTE] + contagem[STATUS.COMPARECE];
  const atencao = contagem[STATUS.IRREGULAR] + contagem[STATUS.NAO_COMPARECE];
  const semInfo = contagem[STATUS.NAO_INFORMADO] + contagem[STATUS.DADO_INCONSISTENTE];
  return {
    total: lista.length,
    frequentes,
    irregulares: contagem[STATUS.IRREGULAR],
    naoComparecem: contagem[STATUS.NAO_COMPARECE],
    atencao,
    semInfo,
    ...contagem,
  };
}

function prepararDados({ membros, regioes, resumo }) {
  const criancas = membros.filter((m) => m.categoria === 'crianca');
  const jovens = membros.filter((m) => m.categoria === 'jovem');
  const atencaoMembros = [...membros]
    .filter((m) => [STATUS.IRREGULAR, STATUS.NAO_COMPARECE].includes(statusDoMembro(m)))
    .sort((a, b) => {
      const cat = (a.categoria === 'crianca' ? 0 : 1) - (b.categoria === 'crianca' ? 0 : 1);
      if (cat !== 0) return cat;
      return getStatusConfig(statusDoMembro(a)).order - getStatusConfig(statusDoMembro(b)).order
        || (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
    });

  const jBat = jovens.filter((m) => m.batizado === true).length;
  const jNBat = jovens.filter((m) => m.batizado === false).length;
  const jNI = jovens.filter((m) => m.batizado == null).length;
  const semLocal = membros.filter((m) => !temCoordenadas(m));

  return {
    atencaoMembros,
    criancas,
    jovens,
    metCriancas: metricasGrupo(criancas),
    metJovens: metricasGrupo(jovens),
    jBat,
    jNBat,
    jNI,
    semLocal,
    resumo,
    regioes,
    membros,
  };
}

function cabecalhoPagina(doc, titulo, subtitulo) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(...RGB.navy);
  doc.rect(0, 0, pageW, 18, 'F');
  doc.setFillColor(...RGB.navyDark);
  doc.rect(0, 18, pageW, 6, 'F');
  doc.setTextColor(...RGB.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(titulo, 12, 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...RGB.tealLight);
  doc.text(subtitulo, 12, 14.5);
}

function novaPagina(doc, hoje, agora) {
  doc.addPage();
  cabecalhoPagina(
    doc,
    'Análise Territorial de Frequência',
    `Continuação · ${hoje} · ${agora}`
  );
  return 28;
}

function rodapePaginas(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...RGB.border);
    doc.setLineWidth(0.2);
    doc.line(12, pageH - 10, pageW - 12, pageH - 10);
    doc.setFontSize(7);
    doc.setTextColor(...RGB.muted);
    doc.text(
      `Sistema Reunião · Análise Territorial · Página ${i} de ${pageCount}`,
      pageW / 2,
      pageH - 5.5,
      { align: 'center' }
    );
  }
}

function bannerSecao(doc, y, texto, cor = RGB.navy) {
  const pageW = doc.internal.pageSize.getWidth();
  const w = pageW - 24;
  doc.setFillColor(...cor);
  doc.roundedRect(12, y, w, 8, 1, 1, 'F');
  doc.setTextColor(...RGB.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(texto, 15, y + 5.3);
  return y + 10;
}

function garantirEspaco(doc, y, minimo = 40, meta) {
  const pageH = doc.internal.pageSize.getHeight();
  if (y > pageH - minimo) {
    return novaPagina(doc, meta.hoje, meta.agora);
  }
  return y;
}

function tabela(doc, y, head, body, {
  columnStyles = {},
  colorir,
  headColor = RGB.teal,
  zebra = true,
} = {}) {
  autoTable(doc, {
    head: [head],
    body,
    startY: y,
    margin: { left: 12, right: 12 },
    styles: {
      font: 'helvetica',
      fontSize: 7,
      cellPadding: { top: 1.5, right: 1.3, bottom: 1.5, left: 1.3 },
      textColor: RGB.ink,
      overflow: 'linebreak',
      lineColor: RGB.border,
      lineWidth: 0.15,
      valign: 'middle',
    },
    headStyles: {
      fillColor: headColor,
      textColor: RGB.white,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'center',
      valign: 'middle',
      cellPadding: { top: 2, right: 1.3, bottom: 2, left: 1.3 },
    },
    alternateRowStyles: zebra ? { fillColor: RGB.soft } : undefined,
    columnStyles,
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      if (colorir) colorir(data);
    },
  });
  return doc.lastAutoTable.finalY + 7;
}

function colorirStatusCatTri(data, mapa) {
  const col = data.column.index;
  const raw = String(data.cell.raw ?? '');
  if (mapa.status != null && col === mapa.status) {
    aplicarEstilo(data.cell, styleStatus(raw));
    return;
  }
  if (mapa.cat != null && col === mapa.cat) {
    aplicarEstilo(data.cell, styleCat(raw));
    return;
  }
  if (mapa.tri && mapa.tri.includes(col)) {
    aplicarEstilo(data.cell, styleTri(raw));
  }
}

/** Cartão grande e simples (lado a lado) para o ancião */
function desenharCartaoGrupo(doc, x, y, w, h, {
  titulo, corTitulo, linhas, rodape,
}) {
  doc.setFillColor(...RGB.white);
  doc.setDrawColor(...RGB.border);
  doc.setLineWidth(0.4);
  doc.roundedRect(x, y, w, h, 2, 2, 'FD');

  doc.setFillColor(...corTitulo);
  doc.roundedRect(x, y, w, 10, 2, 2, 'F');
  doc.rect(x, y + 6, w, 4, 'F');

  doc.setTextColor(...RGB.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(titulo, x + 4, y + 6.8);

  let yy = y + 16;
  linhas.forEach(({ label, valor, cor }) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...RGB.ink);
    doc.text(label, x + 4, yy);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    if (cor) doc.setTextColor(...cor);
    else doc.setTextColor(...RGB.navy);
    doc.text(String(valor), x + w - 4, yy, { align: 'right' });
    yy += 7;
  });

  if (rodape) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.2);
    doc.setTextColor(...RGB.muted);
    doc.text(rodape, x + 4, y + h - 4);
  }
}

function secaoResumo(doc, y, ctx, meta) {
  y = bannerSecao(doc, y, 'RESUMO SIMPLES · PARA ENTENDER RÁPIDO');

  const pageW = doc.internal.pageSize.getWidth();
  const total = ctx.membros.length;
  const atencaoTotal = ctx.atencaoMembros.length;

  // Faixa de visão geral
  doc.setFillColor(...RGB.soft);
  doc.roundedRect(12, y, pageW - 24, 14, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...RGB.navy);
  doc.text(`No total: ${total} pessoa(s)`, 16, y + 5.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...RGB.ink);
  doc.text(
    `Crianças: ${ctx.criancas.length}   ·   Jovens: ${ctx.jovens.length}   ·   Precisam de atenção: ${atencaoTotal}`,
    16,
    y + 11
  );
  y += 18;

  const cardW = (pageW - 24 - 6) / 2;
  const cardH = 64;

  desenharCartaoGrupo(doc, 12, y, cardW, cardH, {
    titulo: `CRIANÇAS  (${ctx.metCriancas.total})`,
    corTitulo: RGB.blue,
    linhas: [
      { label: 'Vão com frequência', valor: ctx.metCriancas.frequentes, cor: RGB.green },
      { label: 'Irregulares', valor: ctx.metCriancas.irregulares, cor: RGB.amber },
      { label: 'Não comparecem', valor: ctx.metCriancas.naoComparecem, cor: RGB.red },
      { label: 'Ainda sem informação', valor: ctx.metCriancas.semInfo, cor: RGB.slate },
      { label: 'Precisam de atenção', valor: ctx.metCriancas.atencao, cor: RGB.red },
    ],
    rodape: 'Atenção = irregulares + não comparecem',
  });

  desenharCartaoGrupo(doc, 12 + cardW + 6, y, cardW, cardH, {
    titulo: `JOVENS  (${ctx.metJovens.total})`,
    corTitulo: RGB.gold,
    linhas: [
      { label: 'Vão com frequência', valor: ctx.metJovens.frequentes, cor: RGB.green },
      { label: 'Irregulares', valor: ctx.metJovens.irregulares, cor: RGB.amber },
      { label: 'Não comparecem', valor: ctx.metJovens.naoComparecem, cor: RGB.red },
      { label: 'Ainda sem informação', valor: ctx.metJovens.semInfo, cor: RGB.slate },
      { label: 'Precisam de atenção', valor: ctx.metJovens.atencao, cor: RGB.red },
      ...(ctx.jovens.length
        ? [{ label: `Batizados / Não / Sem info`, valor: `${ctx.jBat} / ${ctx.jNBat} / ${ctx.jNI}`, cor: RGB.gold }]
        : []),
    ],
    rodape: 'Atenção = irregulares + não comparecem',
  });

  y += cardH + 8;

  // Legenda bem simples
  y = bannerSecao(doc, y, 'CORES · O QUE SIGNIFICA', RGB.slate);
  y = tabela(
    doc,
    y,
    ['Cor / Status', 'Significado (em palavras simples)'],
    [
      ['Frequente / Comparece', 'A pessoa costuma ir às reuniões'],
      ['Irregular', 'Às vezes vai, às vezes não — merece atenção'],
      ['Não comparece', 'Não está indo — prioridade de visita'],
      ['Não informado', 'Ainda não sabemos a situação'],
      ['Sim / Não', 'Resposta do cadastro (frequente, batizado, visita…)'],
    ],
    {
      headColor: RGB.slate,
      zebra: false,
      columnStyles: {
        0: { cellWidth: 55, fontStyle: 'bold', halign: 'center' },
        1: { cellWidth: 'auto' },
      },
      colorir: (data) => {
        if (data.column.index !== 0) return;
        const raw = String(data.cell.raw ?? '');
        if (raw.startsWith('Frequente')) aplicarEstilo(data.cell, styleStatus('Frequente'));
        else if (raw.startsWith('Irregular')) aplicarEstilo(data.cell, styleStatus('Irregular'));
        else if (raw.startsWith('Não comparece')) aplicarEstilo(data.cell, styleStatus('Não comparece'));
        else if (raw.startsWith('Não informado')) aplicarEstilo(data.cell, styleStatus('Não informado'));
        else if (raw.startsWith('Sim')) aplicarEstilo(data.cell, styleTri('Sim'));
      },
    }
  );

  // Indicadores gerais (completo, mas em linguagem clara)
  y = garantirEspaco(doc, y, 55, meta);
  y = bannerSecao(doc, y, 'NÚMEROS GERAIS (COMPLETO)');
  y = tabela(
    doc,
    y,
    ['Indicador', 'Valor', 'Explicação'],
    [
      ['Taxa de comparecimento', pct(ctx.resumo.taxa_comparecimento), 'De quem já tem dado, quantos vão'],
      ['Taxa de atenção', pct(ctx.resumo.taxa_atencao), 'De quem já tem dado, quantos precisam de cuidado'],
      ['Cobertura dos dados', pct(ctx.resumo.cobertura_dados), 'Quantos já têm frequência informada'],
      ['Com endereço no mapa', pct(ctx.resumo.cobertura_coordenadas), 'Quantos têm localização GPS'],
      ['Bairros no relatório', String(ctx.regioes.length), 'Regiões usadas no agrupamento'],
      ['Sem localização GPS', String(ctx.semLocal.length), 'Estão nas listas, mas não no mapa'],
    ],
    {
      columnStyles: {
        0: { cellWidth: 55, fontStyle: 'bold' },
        1: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 'auto', textColor: RGB.muted },
      },
      colorir: (data) => {
        if (data.column.index !== 1) return;
        const ind = String(data.row.raw?.[0] || '');
        const val = String(data.cell.raw ?? '');
        if (ind.includes('comparecimento')) aplicarEstilo(data.cell, styleTaxa(val, 'comparecimento'));
        if (ind.includes('atenção')) aplicarEstilo(data.cell, styleTaxa(val, 'atencao'));
      },
    }
  );

  return y;
}

function secaoBairros(doc, y, ctx) {
  y = bannerSecao(doc, y, `POR BAIRRO · ${ctx.regioes.length} regiões`);
  y = tabela(
    doc,
    y,
    ['Bairro', 'Total', 'Freq.', 'Comp.', 'Irreg.', 'Não comp.', 'Taxa comp.', 'Taxa aten.', 'Atenção'],
    ctx.regioes.map((r) => [
      r.bairro || '—',
      String(r.total),
      String(r.frequentes),
      String(r.comparecem),
      String(r.irregulares),
      String(r.nao_comparecem),
      pct(r.taxa_comparecimento),
      pct(r.taxa_atencao),
      r.quantidade_atencao > 0 ? String(r.quantidade_atencao) : 'OK',
    ]),
    {
      columnStyles: {
        0: { cellWidth: 42, fontStyle: 'bold' },
        1: { cellWidth: 14, halign: 'center' },
        2: { cellWidth: 14, halign: 'center' },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 14, halign: 'center' },
        5: { cellWidth: 18, halign: 'center' },
        6: { cellWidth: 20, halign: 'center' },
        7: { cellWidth: 20, halign: 'center' },
        8: { cellWidth: 18, halign: 'center' },
      },
      colorir: (data) => {
        const col = data.column.index;
        const raw = String(data.cell.raw ?? '');
        if (col === 6) aplicarEstilo(data.cell, styleTaxa(raw, 'comparecimento'));
        if (col === 7) aplicarEstilo(data.cell, styleTaxa(raw, 'atencao'));
        if (col === 8) {
          if (raw === 'OK') aplicarEstilo(data.cell, { fg: RGB.green, bg: RGB.greenBg });
          else aplicarEstilo(data.cell, { fg: RGB.red, bg: RGB.redBg });
        }
      },
    }
  );
  return y;
}

function listaPessoasGrupo(doc, y, lista, {
  titulo, cor, comBatismo = false, meta,
}) {
  y = garantirEspaco(doc, y, 40, meta);
  y = bannerSecao(doc, y, titulo, cor);

  if (!lista.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...RGB.muted);
    doc.text('Nenhuma pessoa neste grupo com os filtros atuais.', 14, y);
    return y + 8;
  }

  const head = comBatismo
    ? ['Nome', 'Status', 'Idade', 'Bairro', 'Endereço', 'Responsável', 'Telefone', 'Freq.?', 'Comp.?', 'Bat.?']
    : ['Nome', 'Status', 'Idade', 'Bairro', 'Endereço', 'Responsável', 'Telefone', 'Freq.?', 'Comp.?'];

  const body = lista.map((m) => {
    const base = [
      m.nome || '—',
      statusLabel(m),
      m.idade != null ? String(m.idade) : '—',
      bairroDe(m),
      enderecoDe(m),
      m.responsavel || '—',
      telefoneDe(m),
      simNao(m.frequente_reuniao),
      simNao(m.comparece_reuniao),
    ];
    if (comBatismo) base.push(simNao(m.batizado));
    return base;
  });

  return tabela(doc, y, head, body, {
    headColor: cor,
    columnStyles: comBatismo
      ? {
          0: { cellWidth: 28, fontStyle: 'bold' },
          1: { cellWidth: 22 },
          2: { cellWidth: 10, halign: 'center' },
          3: { cellWidth: 20 },
          4: { cellWidth: 48 },
          5: { cellWidth: 24 },
          6: { cellWidth: 22 },
          7: { cellWidth: 12, halign: 'center' },
          8: { cellWidth: 12, halign: 'center' },
          9: { cellWidth: 12, halign: 'center' },
        }
      : {
          0: { cellWidth: 30, fontStyle: 'bold' },
          1: { cellWidth: 22 },
          2: { cellWidth: 10, halign: 'center' },
          3: { cellWidth: 22 },
          4: { cellWidth: 52 },
          5: { cellWidth: 26 },
          6: { cellWidth: 22 },
          7: { cellWidth: 13, halign: 'center' },
          8: { cellWidth: 13, halign: 'center' },
        },
    colorir: (data) => colorirStatusCatTri(data, {
      status: 1,
      tri: comBatismo ? [7, 8, 9] : [7, 8],
    }),
  });
}

function secaoPessoas(doc, y, ctx, meta) {
  // Crianças e jovens em listas separadas (mais claro para o ancião)
  y = listaPessoasGrupo(doc, y, ctx.criancas, {
    titulo: `LISTA DE CRIANÇAS · ${ctx.criancas.length}`,
    cor: RGB.blue,
    comBatismo: false,
    meta,
  });

  y = novaPagina(doc, meta.hoje, meta.agora);

  y = listaPessoasGrupo(doc, y, ctx.jovens, {
    titulo: `LISTA DE JOVENS · ${ctx.jovens.length}`,
    cor: RGB.gold,
    comBatismo: true,
    meta,
  });

  return y;
}

function secaoAtencao(doc, y, ctx, meta) {
  y = bannerSecao(doc, y, `QUEM PRECISA DE ATENÇÃO · ${ctx.atencaoMembros.length} pessoa(s)`, RGB.red);

  if (!ctx.atencaoMembros.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...RGB.muted);
    doc.text('Nenhuma pessoa em atenção com os filtros atuais.', 12, y);
    return y + 8;
  }

  const criancasAten = ctx.atencaoMembros.filter((m) => m.categoria === 'crianca');
  const jovensAten = ctx.atencaoMembros.filter((m) => m.categoria === 'jovem');

  doc.setFillColor(...RGB.redBg);
  doc.roundedRect(12, y, doc.internal.pageSize.getWidth() - 24, 8, 1, 1, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...RGB.red);
  doc.text(
    `Crianças: ${criancasAten.length}   ·   Jovens: ${jovensAten.length}   ·   (Irregulares + não comparecem)`,
    15,
    y + 5.3
  );
  y += 11;

  const renderBloco = (lista, titulo, cor) => {
    if (!lista.length) return y;
    y = garantirEspaco(doc, y, 35, meta);
    y = bannerSecao(doc, y, titulo, cor);
    y = tabela(
      doc,
      y,
      ['Nome', 'Status', 'Idade', 'Bairro', 'Endereço', 'Responsável', 'Telefone', 'Visita?', 'Aceitou?'],
      lista.map((m) => [
        m.nome || '—',
        statusLabel(m),
        m.idade != null ? String(m.idade) : '—',
        bairroDe(m),
        enderecoDe(m),
        m.responsavel || '—',
        telefoneDe(m),
        simNao(m.visita_indicada),
        simNao(m.aceitou_visita),
      ]),
      {
        headColor: cor,
        columnStyles: {
          0: { cellWidth: 30, fontStyle: 'bold' },
          1: { cellWidth: 22 },
          2: { cellWidth: 10, halign: 'center' },
          3: { cellWidth: 22 },
          4: { cellWidth: 50 },
          5: { cellWidth: 26 },
          6: { cellWidth: 22 },
          7: { cellWidth: 14, halign: 'center' },
          8: { cellWidth: 14, halign: 'center' },
        },
        colorir: (data) => {
          const col = data.column.index;
          const raw = String(data.cell.raw ?? '');
          if (col === 1) {
            aplicarEstilo(data.cell, styleStatus(raw));
            return;
          }
          if (col === 7 || col === 8) {
            aplicarEstilo(data.cell, styleTri(raw));
            return;
          }
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? [255, 247, 247] : RGB.redBg;
        },
      }
    );
    return y;
  };

  y = renderBloco(criancasAten, `CRIANÇAS QUE PRECISAM DE ATENÇÃO · ${criancasAten.length}`, RGB.blue);
  if (criancasAten.length && jovensAten.length) {
    y = novaPagina(doc, meta.hoje, meta.agora);
  }
  y = renderBloco(jovensAten, `JOVENS QUE PRECISAM DE ATENÇÃO · ${jovensAten.length}`, [146, 64, 14]);
  return y;
}

function secaoJovens(doc, y, ctx, meta) {
  y = bannerSecao(doc, y, `JOVENS E BATISMO · ${ctx.jovens.length} jovem(ns)`, RGB.gold);

  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(...RGB.goldBg);
  doc.roundedRect(12, y, pageW - 24, 8, 1, 1, 'F');
  doc.setTextColor(...RGB.gold);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(
    `Batizados: ${ctx.jBat}   |   Não batizados: ${ctx.jNBat}   |   Não informado: ${ctx.jNI}`,
    15,
    y + 5.3
  );
  y += 11;

  const ordenados = [...ctx.jovens].sort((a, b) => {
    const ord = (m) => (m.batizado === false ? 0 : m.batizado == null ? 1 : 2);
    return ord(a) - ord(b) || (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
  });

  if (!ordenados.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...RGB.muted);
    doc.text('Nenhum jovem com os filtros atuais.', 12, y);
    return y + 8;
  }

  y = tabela(
    doc,
    y,
    ['Nome', 'Status', 'Idade', 'Bairro', 'Endereço', 'Batizado?', 'Visita?', 'Aceitou?', 'Responsável', 'Telefone'],
    ordenados.map((m) => [
      m.nome || '—',
      statusLabel(m),
      m.idade != null ? String(m.idade) : '—',
      bairroDe(m),
      enderecoDe(m),
      simNao(m.batizado),
      simNao(m.visita_indicada),
      simNao(m.aceitou_visita),
      m.responsavel || '—',
      telefoneDe(m),
    ]),
    {
      headColor: [146, 64, 14],
      columnStyles: {
        0: { cellWidth: 28, fontStyle: 'bold' },
        1: { cellWidth: 20 },
        2: { cellWidth: 10, halign: 'center' },
        3: { cellWidth: 20 },
        4: { cellWidth: 46 },
        5: { cellWidth: 16, halign: 'center' },
        6: { cellWidth: 14, halign: 'center' },
        7: { cellWidth: 14, halign: 'center' },
        8: { cellWidth: 24 },
        9: { cellWidth: 22 },
      },
      colorir: (data) => colorirStatusCatTri(data, { status: 1, tri: [5, 6, 7] }),
    }
  );
  return y;
}

function secaoSemLocal(doc, y, ctx) {
  y = bannerSecao(doc, y, `SEM LOCALIZAÇÃO GPS · ${ctx.semLocal.length} membro(s)`, RGB.slate);
  if (!ctx.semLocal.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...RGB.muted);
    doc.text('Todos os membros filtrados possuem coordenadas.', 12, y);
    return y + 8;
  }
  y = tabela(
    doc,
    y,
    ['Nome', 'Status', 'Grupo', 'Idade', 'Bairro', 'Endereço', 'Responsável', 'Telefone'],
    ctx.semLocal.map((m) => [
      m.nome || '—',
      statusLabel(m),
      catLabel(m),
      m.idade != null ? String(m.idade) : '—',
      bairroDe(m),
      enderecoDe(m),
      m.responsavel || '—',
      telefoneDe(m),
    ]),
    {
      headColor: RGB.slate,
      columnStyles: {
        0: { cellWidth: 30, fontStyle: 'bold' },
        1: { cellWidth: 22 },
        2: { cellWidth: 16 },
        3: { cellWidth: 10, halign: 'center' },
        4: { cellWidth: 22 },
        5: { cellWidth: 55 },
        6: { cellWidth: 26 },
        7: { cellWidth: 22 },
      },
      colorir: (data) => colorirStatusCatTri(data, { status: 1, cat: 2 }),
    }
  );
  return y;
}

/**
 * Gera PDF da Análise Territorial — simples para o ancião, completo nos detalhes.
 */
export async function exportarAnaliseFrequenciaPdf({
  membros = [],
  regioes = [],
  resumo = {},
  abas = ['resumo', 'bairros', 'pessoas', 'atencao', 'jovens', 'sem_local'],
}) {
  const incluir = new Set(abas);
  if (!incluir.size) throw new Error('Selecione ao menos uma seção para exportar.');

  const ctx = prepararDados({ membros, regioes, resumo });
  const agora = new Date().toLocaleString('pt-BR');
  const hoje = new Date().toLocaleDateString('pt-BR');
  const meta = { hoje, agora };
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  cabecalhoPagina(
    doc,
    'Análise Territorial de Frequência',
    `Sistema Reunião · Areiópolis, SP · ${hoje} · ${membros.length} pessoa(s) · Gerado em ${agora}`
  );

  let y = 28;
  let secoes = 0;

  const runners = [
    ['resumo', secaoResumo],
    ['bairros', secaoBairros],
    ['pessoas', secaoPessoas],
    ['atencao', secaoAtencao],
    ['jovens', secaoJovens],
    ['sem_local', secaoSemLocal],
  ];

  runners.forEach(([id, fn]) => {
    if (!incluir.has(id)) return;
    if (secoes > 0) {
      y = novaPagina(doc, hoje, agora);
    }
    y = fn(doc, y, ctx, meta);
    secoes += 1;
  });

  if (!secoes) throw new Error('Nenhuma seção pôde ser gerada com a seleção atual.');

  rodapePaginas(doc);
  doc.save(`analise_frequencia_${new Date().toISOString().slice(0, 10)}.pdf`);

  return {
    pessoas: membros.length,
    regioes: regioes.length,
    secoes,
  };
}
